import { MarkdownEditor } from '@/components/MarkdownEditor'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useCreateSkill } from '@/hooks'
import { getErrorMessage } from '@/api/errors'
import { extractValidationMessage } from '@/lib/validationErrors'
import type { CreateSkillRequest } from '@/types'
import { SKILL_CONTENT_MAX, SKILL_DESCRIPTION_MAX, SKILL_NAME_MAX } from '@/lib/skillLimits'
import { toSlug } from '@/lib/slug'
import { Check } from 'lucide-react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'

const labelClass = 'text-xs font-bold leading-[160%] tracking-[0.015em] text-foreground'

export interface CreateSkillInitialValues {
  displayName: string
  description: string
  content: string
}

interface CreateSkillDialogProps {
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  initialValues?: CreateSkillInitialValues
  title?: string
}

interface CreateSkillFormValues {
  displayName: string
  description: string
  content: string
}

export function CreateSkillDialog({ projectId, open, onOpenChange, initialValues, title }: CreateSkillDialogProps) {
  const createSkill = useCreateSkill(projectId)
  const { register, handleSubmit, reset, control, formState: { errors } } = useForm<CreateSkillFormValues>({
    values: {
      displayName: initialValues?.displayName ?? '',
      description: initialValues?.description ?? '',
      content: initialValues?.content ?? '',
    },
  })

  const displayNameValue = useWatch({ control, name: 'displayName' })
  const slug = toSlug(displayNameValue ?? '')

  const onSubmit = async (data: CreateSkillFormValues) => {
    const name = toSlug(data.displayName)
    if (!name) {
      toast.error('Enter a display name so we can generate the skill identifier.')
      return
    }
    try {
      const description = data.description?.trim()
      const payload: CreateSkillRequest = {
        name,
        displayName: data.displayName.trim(),
        content: data.content.trim(),
        ...(description ? { description } : {}),
      }
      await createSkill.mutateAsync(payload)
      toast.success('Skill created successfully')
      reset()
      onOpenChange(false)
    } catch (error) {
      toast.error(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to create skill')
    }
  }

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) reset()
    onOpenChange(isOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[100vw] w-[100vw] h-[100vh] max-h-[100vh] flex flex-col p-0 rounded-none border-none gap-0">
        <DialogHeader className="px-8 py-4 border-b shrink-0 bg-background">
          <DialogTitle className="text-xl font-bold">{title ?? 'Create New Skill'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 flex flex-col overflow-hidden bg-input-background">
            <div className="max-w-4xl w-full mx-auto py-8 px-6 flex-1 flex flex-col min-h-0">
              <div className="rounded-2xl border border-border bg-card p-8 flex flex-col gap-2 flex-1 min-h-0">
                <div className="grid grid-cols-[1fr_1fr_140px] gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="create-displayName" className={labelClass}>Display Name <span className="text-red-500">*</span></Label>
                    <Input
                      id="create-displayName"
                      {...register('displayName', {
                        required: 'Display name is required',
                        maxLength: { value: SKILL_NAME_MAX, message: `Display name must not exceed ${SKILL_NAME_MAX} characters` },
                        validate: value =>
                          toSlug(value).length > 0 || 'Use letters or numbers so an identifier can be generated.',
                      })}
                      placeholder="e.g. API Error Handling"
                    />
                    {errors.displayName && <p className="text-sm text-destructive" role="alert">{errors.displayName.message}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label className={labelClass}>Internal Name</Label>
                    <div className="h-9 flex items-center rounded-md border border-border bg-muted px-3 font-mono text-sm text-muted-foreground" data-testid="skill-slug-preview">
                      {slug || '—'}
                    </div>
                    <p className="text-xs text-muted-foreground">Generated from the display name. It can't be changed later.</p>
                  </div>
                  <div className="space-y-2">
                    <Label className={labelClass}>Version</Label>
                    <div className="h-9 flex items-center rounded-md border border-border bg-muted px-3 font-mono text-sm text-muted-foreground" data-testid="skill-version-preview">
                      —
                    </div>
                    <p className="text-xs text-muted-foreground">Assigned automatically on save.</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="create-description" className={labelClass}>Description</Label>
                  <Textarea
                    id="create-description"
                    {...register('description', {
                      maxLength: { value: SKILL_DESCRIPTION_MAX, message: `Description must not exceed ${SKILL_DESCRIPTION_MAX} characters` },
                    })}
                    placeholder="One line on what this skill teaches and when it applies."
                    rows={3}
                  />
                  {errors.description && <p className="text-sm text-destructive">{errors.description.message}</p>}
                </div>
                <div className="flex flex-col gap-2 flex-1 min-h-0">
                  <Label htmlFor="content" className={labelClass}>Content (SKILL.md) <span className="text-red-500">*</span></Label>
                  <Controller
                    name="content"
                    control={control}
                    rules={{
                      required: 'Content is required',
                      maxLength: { value: SKILL_CONTENT_MAX, message: `Content must not exceed ${SKILL_CONTENT_MAX} characters` },
                    }}
                    render={({ field }) => (
                      <MarkdownEditor
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="What the agent should know..."
                        className="flex-1 flex flex-col min-h-0 [&>div:last-child]:flex-1 [&>div:last-child]:overflow-y-auto"
                      />
                    )}
                  />
                  {errors.content && <p className="text-sm text-destructive">{errors.content.message}</p>}
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="ghost" onClick={() => handleClose(false)}>Cancel</Button>
                  <Button type="submit" disabled={createSkill.isPending}>
                    <Check className="h-4 w-4" />
                    {createSkill.isPending ? 'Creating...' : 'Create Skill'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
